import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Plus, Trash2, X } from 'lucide-react';
import { apiBase, authHeaders, getToken, resolveOrgId } from '../utils/authHeaders';

export const ckApi = axios.create({ baseURL: `${apiBase()}/api/ck-ops` });
export function readCkScope() {
  return { orgId: resolveOrgId(), token: getToken(), locationId: localStorage.getItem('current_location_id') || '', department: localStorage.getItem('active_department') || '' };
}
export function useCkScope(refreshToken) {
  const [scope, setScope] = useState(readCkScope);
  useEffect(() => {
    const update = () => { const next = readCkScope(); setScope(previous => scopeKey(previous) === scopeKey(next) ? previous : next); };
    update();
    const events = ['organizationChanged', 'locationChanged', 'fi-department-changed', 'storage'];
    events.forEach(e => window.addEventListener(e, update));
    return () => events.forEach(e => window.removeEventListener(e, update));
  }, [refreshToken]);
  return scope;
}
export const scopeKey = scope => JSON.stringify(scope);
export const requestConfig = (scope, signal) => ({ headers: authHeaders(scope), signal });
export function useCkNavEnabled(token) {
  const scope = useCkScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (scope.orgId && scope.locationId && scope.token) {
      ckApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(() => { if (!controller.signal.aborted) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope]);
  return allowedKey === key;
}
export function parseLotPayload(value) {
  const match = /^LOT:([^\s\x00-\x1f]+)$/.exec(value.trim());
  if (!match) throw new Error('Enter a LOT: barcode payload.');
  return match[1];
}
export function Field({ label, options, ...props }) {
  return <label className="ck-field"><span>{label}</span>{options ? <select aria-label={label} {...props}>{options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select> : <input aria-label={label} {...props} />}</label>;
}
export const decimalProps = { type: 'text', inputMode: 'decimal', pattern: '[0-9]+([.][0-9]+)?' };
export function commandId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `ck-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
}
export function Form({ title, submit, onSubmit, busy, children }) {
  return <form className="ck-form" aria-label={title} onSubmit={e => { e.preventDefault(); onSubmit(); }}><h2>{title}</h2><fieldset disabled={busy}><div className="ck-fields">{children}</div><button className="ck-primary" type="submit">{submit}</button></fieldset></form>;
}
export function Rows({ rows, setRows, fields, blank, label, allowAdd = true }) {
  return <div className="ck-rows"><h3>{label}</h3>{rows.map((row, index) => <div className="ck-line" key={index}>{fields.map(({ key, ...field }) => <Field key={key} {...field} label={`${field.label} ${index + 1}`} value={row[key] ?? ''} onChange={e => setRows(rows.map((r, i) => i === index ? { ...r, [key]: e.target.value } : r))} />)}<button type="button" title="Remove line" aria-label={`Remove ${label} ${index + 1}`} disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, i) => i !== index))}><Trash2 size={16} /></button></div>)}{allowAdd && <button type="button" onClick={() => setRows([...rows, { ...blank }])}><Plus size={16} /> Add line</button>}</div>;
}
export function Table({ headers, children, empty }) {
  return <div className="ck-table-wrap" tabIndex={0} role="region" aria-label={`${headers[0]} records`}><table><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{children}</tbody></table>{empty && <p className="ck-empty">No records at this site.</p>}</div>;
}
export function LotScanner({ onScan, busy }) {
  const [value, setValue] = useState('');
  const [camera, setCamera] = useState(false);
  const [error, setError] = useState('');
  const video = useRef(null);
  const callback = useRef(onScan);
  callback.current = onScan;
  useEffect(() => {
    if (!camera) return undefined;
    let stopped = false, stream, timer;
    const stop = () => { stopped = true; clearTimeout(timer); stream?.getTracks().forEach(t => t.stop()); };
    (async () => {
      try {
        if (!window.BarcodeDetector || !navigator.mediaDevices?.getUserMedia) throw new Error('Camera scanning is unavailable. Enter the LOT: payload below.');
        const formats = await window.BarcodeDetector.getSupportedFormats();
        if (!formats.includes('code_128')) throw new Error('Code 128 scanning is unavailable. Enter the LOT: payload below.');
        const detector = new window.BarcodeDetector({ formats: ['code_128'] });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
        video.current.srcObject = stream;
        await video.current.play();
        const scan = async () => {
          if (stopped) return;
          try {
            const codes = await detector.detect(video.current);
            if (stopped) return;
            const code = codes.find(c => /^LOT:/.test(c.rawValue));
            if (code) { const id = parseLotPayload(code.rawValue); stop(); setCamera(false); setValue(code.rawValue); callback.current(id); return; }
            timer = setTimeout(scan, 250);
          } catch { if (!stopped) { stop(); setCamera(false); setError('Camera scanning failed. Enter the LOT: payload below.'); } }
        };
        scan();
      } catch (e) { if (!stopped) { stop(); setCamera(false); setError(e.message || 'Camera access denied. Enter the LOT: payload below.'); } }
    })();
    return stop;
  }, [camera]);
  return <section className="ck-scan"><form onSubmit={e => { e.preventDefault(); try { setError(''); onScan(parseLotPayload(value)); } catch (err) { setError(err.message); } }}><Field label="Lot barcode" value={value} onChange={e => setValue(e.target.value)} required /><button disabled={busy} type="submit">Find lot</button><button type="button" disabled={busy} onClick={() => { setError(''); setCamera(!camera); }}>{camera ? <X size={18} /> : <Camera size={18} />}{camera ? 'Stop camera' : 'Scan'}</button></form>{camera && <video ref={video} muted playsInline aria-label="Lot barcode camera" />}{error && <p role="alert">{error}</p>}</section>;
}
